import React, { useEffect, useState } from "react";
import { Users, IndianRupee, Coins, Recycle, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import api, { money } from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";

const COLORS = ["#D95D39", "#2A3C24", "#F4A261"];

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setD(data)); }, []);

  if (!d) return <AdminLayout title="Dashboard"><div className="text-muted-foreground">Loading…</div></AdminLayout>;

  const salesData = [
    { name: "Rice", value: d.sales.rice }, { name: "Grains", value: d.sales.grains }, { name: "Pulses", value: d.sales.pulses },
  ];

  return (
    <AdminLayout title="Dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-kpis">
        <Kpi icon={Users} label="Total Customers" value={d.customers.total} />
        <Kpi icon={IndianRupee} label="Total Sales" value={money(d.sales.total)} />
        <Kpi icon={Coins} label="Points Outstanding" value={money(d.ricepoints.outstanding)} />
        <Kpi icon={Recycle} label="Kg Collected" value={`${d.tradein.kg_collected} kg`} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <Panel title="Sales by Category">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={salesData}>
              <XAxis dataKey="name" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => money(v)} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>{salesData.map((e, i) => <Cell key={i} fill={COLORS[i]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="RicePoints Flow">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={[{ name: "Issued", value: d.ricepoints.issued }, { name: "Redeemed", value: d.ricepoints.redeemed }, { name: "Outstanding", value: d.ricepoints.outstanding }]}
                   dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {[0, 1, 2].map((i) => <Cell key={i} fill={COLORS[i]} />)}
              </Pie>
              <Tooltip formatter={(v) => money(v)} />
            </PieChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-4">
        <MetricPanel title="RicePoints" rows={[
          ["Points issued", money(d.ricepoints.issued)],
          ["Points redeemed", money(d.ricepoints.redeemed)],
          ["Outstanding (liability)", money(d.ricepoints.outstanding)],
          ["Redemption rate", `${d.ricepoints.redemption_rate}%`],
          ["Avg balance / customer", money(d.ricepoints.avg_balance)],
        ]} />
        <MetricPanel title="Trade-In" rows={[
          ["Requests", d.tradein.requests],
          ["Completed pickups", d.tradein.completed],
          ["Kg collected", `${d.tradein.kg_collected} kg`],
          ["Avg kg / customer", `${d.tradein.avg_kg} kg`],
          ["Rejection rate", `${d.tradein.rejection_rate}%`],
          ["Points from trade-ins", money(d.tradein.points_issued)],
        ]} />
        <MetricPanel title="Customer Analytics" rows={[
          ["Active customers", d.customers.active],
          ["Repeat customers", d.customers.repeat],
          ["Repeat purchase rate", `${d.analytics.repeat_rate}%`],
          ["Trade-in participation", `${d.analytics.participation_rate}%`],
          ["Trade→purchase conversion", `${d.analytics.trade_to_purchase}%`],
          ["Average order value", money(d.analytics.aov)],
        ]} />
      </div>

      <div className="mt-4 bg-card border border-border rounded-lg p-5">
        <div className="font-heading font-bold flex items-center gap-2"><TrendingUp size={18} className="text-primary" /> Profitability snapshot</div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          {[["Revenue", d.profitability.revenue], ["Est. gross margin", d.profitability.gross_margin], ["Points redeemed", d.profitability.points_redeemed], ["Est. contribution", d.profitability.estimated_contribution]].map(([l, v]) => (
            <div key={l} className="bg-muted rounded-md p-3"><div className="text-xs text-muted-foreground">{l}</div><div className="font-mono-points font-bold text-lg">{money(v)}</div></div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center gap-2 text-muted-foreground text-xs"><Icon size={15} /> {label}</div>
      <div className="font-heading font-extrabold text-2xl mt-1 font-mono-points">{value}</div>
    </div>
  );
}
function Panel({ title, children }) {
  return <div className="bg-card border border-border rounded-lg p-5"><div className="font-heading font-bold mb-2">{title}</div>{children}</div>;
}
function MetricPanel({ title, rows }) {
  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <div className="font-heading font-bold mb-3">{title}</div>
      <div className="space-y-2 text-sm">
        {rows.map(([l, v]) => <div key={l} className="flex justify-between"><span className="text-muted-foreground">{l}</span><span className="font-mono-points font-semibold">{v}</span></div>)}
      </div>
    </div>
  );
}
